// api.luwu.dev as the signed-in staff member. The token is this site's own
// (localStorage "luwu-admin-token"), from a sign-in by email code at
// accounts.luwu.dev. 401 sends you to sign in; 403 on a read means you're
// not on the staff, 403 on a write means your role can't do it.

import type { Me } from "./types";
import { $, store, view } from "./ui";

export const LUWU = "https://api.luwu.dev";
export const ACCOUNTS = "https://accounts.luwu.dev";
const TOKEN = "luwu-admin-token";

let token = store.get(TOKEN);
export const signedIn = () => !!token;
export function setToken(t: string | null) {
  token = t;
  store.set(TOKEN, t);
  if (!t) current = null;
}

let current: Me | null = null;
export const me = () => current;
export const role = () => current?.role ?? "viewer";
export const canWrite = () => role() === "owner" || role() === "admin";
export const isOwner = () => role() === "owner";

/** Loads who's signed in (GET /v1/admin/me). Null means the view already changed. */
export async function loadMe(): Promise<Me | null> {
  current = await api<Me>("/v1/admin/me");
  return current;
}

/** A read. On trouble it switches the view (sign-in, denied, or a message) and answers null. */
export async function api<T>(path: string): Promise<T | null> {
  let r: Response;
  try {
    r = await fetch(LUWU + path, { headers: { authorization: `Bearer ${token}` } });
  } catch {
    $("loading-msg").textContent = "Couldn't reach Luwu. Check your connection and reload.";
    view("loading");
    return null;
  }
  if (r.status === 401) {
    setToken(null);
    view("email");
    return null;
  }
  if (r.status === 403) {
    const data = (await r.json().catch(() => ({}))) as { error?: string };
    if (data.error) $("denied-msg").textContent = data.error;
    view("denied");
    return null;
  }
  if (!r.ok) {
    const data = (await r.json().catch(() => ({}))) as { error?: string };
    $("loading-msg").textContent = data.error ? `Luwu answered ${r.status}: ${data.error}. Reload to try again.` : `Luwu answered ${r.status}. Reload to try again.`;
    view("loading");
    return null;
  }
  return (await r.json()) as T;
}

/** A change at api.luwu.dev; throws the server's message for the flash line. */
export async function send<T>(method: string, path: string, body?: unknown): Promise<T> {
  let r: Response;
  try {
    r = await fetch(LUWU + path, {
      method,
      headers: { authorization: `Bearer ${token}`, ...(body !== undefined ? { "content-type": "application/json" } : {}) },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new Error("Couldn't reach Luwu. Check your connection and try again.");
  }
  if (r.status === 401) {
    setToken(null);
    view("email");
    throw new Error("Your sign-in has expired. Sign in again.");
  }
  const data = (await r.json().catch(() => ({}))) as T & { error?: string; issues?: { path: (string | number)[]; message: string }[] };
  if (!r.ok) {
    const issue = data.issues?.[0];
    throw new Error(issue ? `${issue.path.join(".") || "request"}: ${issue.message}` : (data.error ?? `Luwu answered ${r.status}`));
  }
  return data;
}

/** A download (the CSV export): the Bearer header can't ride on a link, so fetch it and hand the browser a blob. */
export async function download(path: string, fallbackName: string) {
  const r = await fetch(LUWU + path, { headers: { authorization: `Bearer ${token}` } });
  if (!r.ok) {
    const data = (await r.json().catch(() => ({}))) as { error?: string };
    throw new Error(data.error ?? `Luwu answered ${r.status}`);
  }
  const name = /filename="([^"]+)"/.exec(r.headers.get("content-disposition") ?? "")?.[1] ?? fallbackName;
  const url = URL.createObjectURL(await r.blob());
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** The sign-in server (no token). */
export async function post<T>(url: string, body: unknown): Promise<T> {
  let r: Response;
  try {
    r = await fetch(url, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body) });
  } catch {
    throw new Error("Couldn't reach the sign-in server. Check your connection.");
  }
  const data = (await r.json().catch(() => ({}))) as T & { error?: string; message?: string };
  if (!r.ok) throw new Error(r.status === 429 ? "Too many attempts. Wait a minute and try again." : (data.message ?? data.error ?? `Sign-in failed (${r.status})`));
  return data;
}

export const query = (params: Record<string, string | number | undefined>) =>
  new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== "").map(([k, v]) => [k, String(v)]))).toString();
